const trimValue = (value?: string | null) => value?.trim() ?? "";

export const resolveDownloadFileName = (contentDisposition: string | null, fallback: string): string => {
    if (!contentDisposition) {
        return fallback;
    }

    const encodedMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (encodedMatch?.[1]) {
        return decodeURIComponent(encodedMatch[1].trim().replace(/^"|"$/g, ""));
    }

    const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return plainMatch?.[1]?.trim() || fallback;
};

// Shared REST error parser: JSON string bodies, `errors` (array or validation map),
// then `message`/`detail`/`title`, then plain text, then the fallback.
export const readDownloadErrorMessage =async (response: Response, fallback: string): Promise<string> => {
    const body = await response.text();
    if (!body) {
        return fallback;
    }

    try {
        const payload = JSON.parse(body);
        if (typeof payload === "string") {
            return trimValue(payload) || fallback;
        }

        const errors = Array.isArray(payload?.errors)
            ? payload.errors
            : Object.values(payload?.errors ?? {}).flat();
        const firstError = errors
            .map((error: any) => typeof error === "string" ? error : error?.message)
            .find((value: unknown) => typeof value === "string" && trimValue(value));

        return trimValue(firstError)
            || trimValue(payload?.message)
            || trimValue(payload?.detail)
            || trimValue(payload?.title)
            || fallback;
    } catch {
        return trimValue(body) || fallback;
    }
};

export const downloadResponseFile = async (response: Response, fallbackFileName: string): Promise<string> => {
    const blob = await response.blob();
    if (!blob.size) {
        throw new Error("Downloaded file is empty.");
    }

    const fileName = resolveDownloadFileName(
        response.headers.get("content-disposition"),
        fallbackFileName);
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
    return fileName;
};
